import * as React from 'react';
import * as moment from 'moment';
import accountProvider from "../account/AccountProvider";
import locales from "../locales";
import {DateRange} from "../common/dtos";
import {FormComponentProps} from "antd/lib/form";
import {Form} from 'antd';
import SubmitCancelFormItem from "../common/form-items/SubmitCancelFormItem";
import TextAreaFormItem from "../common/form-items/TextAreaFormItem";
import ClientContactSelector from "../client-details/ClientContactSelector";
import ClientSiteSelector from "../client-details/ClientSiteSelector";
import ClientSelector from "../client-details/ClientSelector";
import HiddenFormItem from "../common/form-items/HiddenFormItem";
import {ClientSummary, Contact, EventType, Site} from "../api/dtos/dtos";
import SalesUserSelector from "./SalesUserSelector";
import EventTimeRangePicker from "./EventTimeRangePicker";
import {combineMoments, MOMENT_DATE_TIME_FORMAT, MOMENT_TIME_FORMAT, now, toStartOfDay,} from "../utils/date-utils";
import {EventDto} from "./dtos";
import InputFormItem from "../common/form-items/InputFormItem";
import CheckBoxFormItem from "../common/form-items/CheckBoxFormItem";
import {CheckboxChangeEvent} from "antd/lib/checkbox";
import DatePickerFormItem from "../common/form-items/DatePickerFormItem";
import SelectFormItem from "../common/form-items/SelectFormItem";
import {getEventTypeOptions} from "../client-calendar/EventType";

interface EventFormProps extends FormComponentProps {
  editable: boolean;
  event?: EventDto;
  initialDateRange?: DateRange,
  initialSalesUser?: string;
  onFormCancel: () => void;
  onFormSubmit: (event: EventDto) => void;
}

interface EventFormState {
  salesUser?: string;
  client?: ClientSummary;
  contact?: Contact;
  site?: Site;
  alertDate?: moment.Moment;
  showAlertDateField: boolean;
  completedDisabled: boolean;
}

const FIELD_DATE = 'date';
const FIELD_START = 'start';
const FIELD_END = 'end';
const FIELD_SUBJECT = 'subject';
const FIELD_COMMENT = 'comment';
const FIELD_USER = 'user';
const FIELD_CLIENT = 'client';
const FIELD_CONTACT = 'contact';
const FIELD_SITE = 'site';
const FIELD_ALERT_TIME = 'alert_time';
const FIELD_ALERT_REQUIRED = 'alert_required';
const FIELD_TYPE = 'type';
const FIELD_COMPLETED = 'completed';

class EventForm extends React.Component<EventFormProps, EventFormState> {

  constructor(props: EventFormProps) {
    super(props);
    this.state = {
      salesUser: props.event?.userName ?? props.initialSalesUser,
      client: props.event?.client,
      contact: props.event?.contact,
      site: props.event?.site,
      showAlertDateField: false,
      alertDate: props.event?.alertDate,
      completedDisabled: true,
    };
  }

  public componentDidUpdate(prevProps: Readonly<EventFormProps>, prevState: Readonly<EventFormState>) {
    if (prevProps.event !== this.props.event) {
      this.setState({
        salesUser: this.props.event?.userName,
        client: this.props.event?.client,
        contact: this.props.event?.contact,
        site: this.props.event?.site,
        showAlertDateField: this.props.event?.alertDate != null,
        alertDate: this.props.event?.alertDate,
        completedDisabled: this.isStartAfterNow(this.props.event?.start) ?? true
      });
    }
  }

  public render(): React.ReactNode {
    const {form, editable, event, initialDateRange} = this.props;
    const {salesUser, client, contact, site, showAlertDateField, alertDate} = this.state;
    const disabled = !editable;
    const editMode = event !== undefined;
    const admin = accountProvider.isAdmin();

    return (
      <Form>
        {admin &&
            <HiddenFormItem
                form={form}
                fieldId={FIELD_USER}
                label={locales.event_sales_user}
                initialValue={salesUser}
                required={true}
            >
                <SalesUserSelector
                    user={salesUser}
                    disabled={disabled || editMode}
                    onSelect={this.onSalesUserSelect}
                />
            </HiddenFormItem>
        }
        <SelectFormItem
          form={form}
          fieldId={FIELD_TYPE}
          label={locales.event_type}
          initialValue={(event && event.type) || EventType.PERSONAL_MEETING}
          required={true}
          disabled={disabled}
          options={getEventTypeOptions()}
        />
        <InputFormItem
          form={form}
          fieldId={FIELD_SUBJECT}
          initialValue={event && event.subject}
          label={locales.event_subject}
          required={true}
          disabled={disabled}
        />
        <EventTimeRangePicker
          fieldIdDate={FIELD_DATE}
          fieldIdStart={FIELD_START}
          fieldIdEnd={FIELD_END}
          event={event}
          initialDateRange={initialDateRange}
          role={accountProvider.getRole()!}
          disabled={disabled}
          form={form}
          onStartChanged={this.setCompleted}
        />
        <TextAreaFormItem
          form={form}
          fieldId={FIELD_COMMENT}
          initialValue={event && event.comment}
          label={locales.comment}
          required={false}
          disabled={disabled}
          maxLength={255}
        />
        <HiddenFormItem
          form={form}
          fieldId={FIELD_CLIENT}
          label={locales.client}
          initialValue={client}
          required={false}
          disabled={disabled}
        >
          <ClientSelector
            client={client}
            onSelect={this.onClientSelect}
            editable={editable}
          />
        </HiddenFormItem>
        <HiddenFormItem
          form={form}
          fieldId={FIELD_CONTACT}
          label={locales.client_contact}
          initialValue={contact}
          required={false}
        >
          <ClientContactSelector
            client={client}
            contact={contact}
            disabled={disabled}
            onContactChange={this.onContactChange}
          />
        </HiddenFormItem>
        <HiddenFormItem
          form={form}
          fieldId={FIELD_SITE}
          label={locales.event_location}
          initialValue={site}
          required={false}
        >
          <ClientSiteSelector
            onSiteChange={this.onSiteChange}
            client={client}
            site={site}
            disabled={disabled}
          />
        </HiddenFormItem>
        <CheckBoxFormItem
          form={form}
          fieldId={FIELD_ALERT_REQUIRED}
          disabled={disabled || form.getFieldValue(FIELD_START) === undefined}
          label={locales.event_show_alert}
          initialValue={showAlertDateField}
          required={false}
          onChange={this.onAlertCheckboxChange}
        />

        {showAlertDateField &&
            <DatePickerFormItem
                form={form}
                fieldId={FIELD_ALERT_TIME}
                disabled={disabled}
                initialValue={alertDate}
                label={locales.event_alert_date}
                format={MOMENT_DATE_TIME_FORMAT}
                showTime={{format: MOMENT_TIME_FORMAT}}
                showClear={false}
                required={true}
                disabledDate={this.isAlertDateDisabled}
                rules={[{validator: this.validateAlertDateTime}]}
            />
        }
        <CheckBoxFormItem
          form={form}
          fieldId={FIELD_COMPLETED}
          disabled={disabled || this.state.completedDisabled}
          label={locales.event_completed}
          initialValue={event && event.completed}
          required={false}
        />

        <SubmitCancelFormItem disabled={disabled} onSubmit={this.onFormSubmit} onCancel={this.onFormCancel}/>
      </Form>
    );
  }

  private validateAlertDateTime = (rule: any, value: moment.Moment, callback: (msg?: string) => void) => {
    const startTime = this.getStartTime();
    if (value && startTime && value.isAfter(startTime)) {
      callback(locales.event_alert_date_error);
      return;
    }
    callback();
  }

  private setCompleted = (startTime: moment.Moment | undefined): void => {
    const disabled = this.isStartAfterNow(startTime) ?? true;
    if (disabled) {
      this.props.form.setFieldsValue({[FIELD_COMPLETED]: undefined});
    }
    this.setState({
      completedDisabled: disabled,
    })
  }

  private isStartAfterNow = (startTime: moment.Moment | undefined): boolean | undefined => {
    return startTime?.isAfter(now());
  }

  private isAlertDateDisabled = (current: moment.Moment | null): boolean => {
    const startTime = this.getStartTime();
    if (current === null || startTime === null) {
      return true;
    }

    if (startTime.hour() === 0) {
      return current.isAfter(startTime);
    }

    const nextDay = startTime.clone().add(1, 'day');
    const startOfNextDay = toStartOfDay(nextDay);

    return current.isAfter(startOfNextDay);
  }

  private getStartTime = (): moment.Moment | null => {
    const {form} = this.props;
    const dateValue = form.getFieldValue(FIELD_DATE);
    const timeValue = form.getFieldValue(FIELD_START);
    if (dateValue && timeValue) {
      return combineMoments(dateValue, timeValue);
    }
    return null;
  }

  private onAlertCheckboxChange = (e: CheckboxChangeEvent) => {
    if (!e.target.checked) {
      this.props.form.setFieldsValue({[FIELD_ALERT_TIME]: null});
    }

    this.setState({showAlertDateField: e.target.checked});
  }

  private onSalesUserSelect = (userName: string) => {
    this.props.form.setFieldsValue({[FIELD_USER]: userName});
  }

  private onClientSelect = (client?: ClientSummary) => {
    this.props.form.setFieldsValue({
      [FIELD_CLIENT]: client,
      [FIELD_CONTACT]: undefined,
      [FIELD_SITE]: undefined,
    });
    this.setState({client});
  };

  private onContactChange = (contact: Contact | undefined) => {
    this.props.form.setFieldsValue({[FIELD_CONTACT]: contact});
    this.setState({contact});
  };

  private onSiteChange = (site: Site) => {
    this.props.form.setFieldsValue({[FIELD_SITE]: site});
    this.setState({site});
  };

  private onFormCancel = () => {
    this.props.form.resetFields();
    this.props.onFormCancel();
  };

  private onFormSubmit = () => {
    const {form, event, onFormSubmit} = this.props;
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const date = values[FIELD_DATE];
        const start = values[FIELD_START];
        const end = values[FIELD_END];
        const startTime = combineMoments(date, start);
        const endTime = combineMoments(date, end);

        let alertTime: moment.Moment | undefined;
        if (this.state.showAlertDateField) {
          alertTime = values[FIELD_ALERT_TIME];
        }

        let user;
        if (accountProvider.isAdmin()) {
          user = values[FIELD_USER];
        } else if (event) {
          user = event.userName;
        } else {
          user = accountProvider.getUserName();
        }

        const eventUpdate = new EventDto(
          event ? event.id : null,
          user,
          values[FIELD_CLIENT],
          values[FIELD_CONTACT],
          values[FIELD_SITE],
          values[FIELD_SUBJECT],
          values[FIELD_COMMENT],
          startTime,
          endTime,
          alertTime,
          undefined,
          values[FIELD_TYPE],
          undefined,
          values[FIELD_COMPLETED],
          undefined,
        )
        onFormSubmit(eventUpdate)
      }
    });
  };
}

export default Form.create<EventFormProps>()(EventForm);


// WEBPACK FOOTER //
// ./src/event-details/EventForm.tsx